import { useMutation, useQueryClient } from "@tanstack/react-query";

import { axiosInstance } from "../../lib/axios";
import { INTEGRATION_SCOPES } from "../constants";

import { CRAWLER_PREFERENCE_QUERY_KEY } from "./useFetchCrawlerPreference";

import type { IntegrationScope } from "../constants";

// A refresh re-reads the tree from the provider, which only a tier the caller owns has.
type RefreshableScope = Extract<IntegrationScope, "user" | "deployment">;

interface CrawlerPreferenceTriggerRequest {
  source: string;
  verifiableId: string;
  integrationScope?: RefreshableScope;
}

interface CrawlerPreferenceTriggerResponse {
  success: boolean;
  message?: string;
}

// Hook to trigger crawler preference refetch
export const useCrawlerPreferenceTriggerRefetch = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      source,
      verifiableId,
      integrationScope = INTEGRATION_SCOPES.USER,
    }: CrawlerPreferenceTriggerRequest) => {
      return await axiosInstance.post<CrawlerPreferenceTriggerResponse>(
        "/preference/crawler/trigger",
        {
          source,
          verifiableId,
          integration_scope: integrationScope,
        },
      );
    },
    onSuccess: (response, variables) => {
      // Invalidate and refetch the crawler preference query to get updated data
      queryClient.invalidateQueries({
        queryKey: CRAWLER_PREFERENCE_QUERY_KEY(
          variables.source,
          variables.verifiableId,
          variables.integrationScope ?? INTEGRATION_SCOPES.USER,
        ),
      });

      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};
